<script setup>
import { ref } from 'vue'
import { useNotificationStore } from '../stores/notificationStore'

const notificationStore = useNotificationStore()
const testMessage = ref('tere!')
const testType = ref('message')
const isPublishing = ref(false)
const lastResult = ref(null)

const publishTestMessage = async () => {
  if (!testMessage.value.trim()) {
    alert('Please enter a message')
    return
  }

  isPublishing.value = true
  lastResult.value = null

  try {
    const result = await notificationStore.publishSSEMessage({
      message: testMessage.value,
      type: testType.value,
      data: {
        test: true,
        timestamp: new Date().toISOString()
      }
    })
    
    lastResult.value = {
      success: true,
      data: result
    }
    
    // Clear the input after successful publish
    testMessage.value = 'tere!'
  } catch (error) {
    lastResult.value = {
      success: false,
      error: error.message
    }
  } finally {
    isPublishing.value = false
  }
}

const connectionStatus = notificationStore.isSSEConnected

const testConnection = async () => {
  try {
    await notificationStore.disconnectSSE()
    await new Promise(resolve => setTimeout(resolve, 1000))
    await notificationStore.initializeSSE()
  } catch (error) {
    console.error('Connection test failed:', error)
  }
}
</script>

<template>
  <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold text-gray-800">SSE Test Panel</h3>
      <div 
        class="flex items-center gap-2 px-3 py-1 rounded-full text-sm"
        :class="connectionStatus ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
      >
        <div 
          class="w-2 h-2 rounded-full"
          :class="connectionStatus ? 'bg-green-500' : 'bg-red-500'"
        ></div>
        {{ connectionStatus ? 'Connected' : 'Disconnected' }}
      </div>
    </div>

    <div class="space-y-4">
      <!-- Message Input -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Test Message
        </label>
        <input
          v-model="testMessage"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          placeholder="Enter test message..."
        />
      </div>

      <!-- Message Type -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Message Type
        </label>
        <select
          v-model="testType"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
        >
          <option value="message">Message</option>
          <option value="info">Info</option>
          <option value="success">Success</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
        </select>
      </div>

      <!-- Test Connection Button -->
      <button
        @click="testConnection"
        class="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors mb-2"
      >
        Test Connection
      </button>

      <!-- Publish Button -->
      <button
        @click="publishTestMessage"
        :disabled="isPublishing"
        class="w-full bg-pink-500 hover:bg-pink-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
      >
        <span v-if="isPublishing">Publishing...</span>
        <span v-else>Publish Message</span>
      </button>

      <!-- Result Display -->
      <div v-if="lastResult" class="mt-4">
        <div 
          class="p-3 rounded-lg"
          :class="lastResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'"
        >
          <div class="flex items-center gap-2 mb-2">
            <i 
              :class="lastResult.success ? 'fas fa-check-circle text-green-500' : 'fas fa-exclamation-circle text-red-500'"
            ></i>
            <span 
              class="font-medium"
              :class="lastResult.success ? 'text-green-800' : 'text-red-800'"
            >
              {{ lastResult.success ? 'Success!' : 'Error' }}
            </span>
          </div>
          
          <pre class="text-xs text-gray-600 overflow-auto">{{ 
            lastResult.success 
              ? JSON.stringify(lastResult.data, null, 2) 
              : lastResult.error 
          }}</pre>
        </div>
      </div>

      <!-- Instructions -->
      <div class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
        <p class="font-medium mb-1">How to test:</p>
        <ol class="list-decimal list-inside space-y-1 text-xs">
          <li>Enter a test message above</li>
          <li>Select the message type</li>
          <li>Click "Publish Message"</li>
          <li>Check the notification bell in the top-right corner</li>
          <li>Your message should appear as a new notification!</li>
        </ol>
      </div>
    </div>
  </div>
</template>
